import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./utils";

/**
 * Pill — 디자인 시스템 핵심 버튼 (rounded-full).
 *
 * design-system/ui_kits/global-web/Pill.jsx 참조 구현.
 * shadcn Button을 대체하여 LINK BAND 글로벌 톤(인디고 + 글래시)을 통일.
 */
const pillVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-[-0.01em] whitespace-nowrap transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/50",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--lb-indigo-600)] text-white border border-transparent hover:-translate-y-px hover:shadow-[0_0_30px_rgba(79,70,229,0.5)]",
        secondary:
          "bg-white/[0.05] text-white border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20",
      },
      size: {
        sm: "px-4 py-2 text-[13px]",
        md: "px-6 py-3 text-sm",
        lg: "px-7 py-3.5 text-[15px]",
        xl: "px-9 py-[18px] text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type PillProps = {
  as?: "button" | "a";
  className?: string;
  children?: React.ReactNode;
} & VariantProps<typeof pillVariants> &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children">;

const Pill = React.forwardRef<HTMLElement, PillProps>(
  ({ as = "button", variant, size, className, children, ...props }, ref) => {
    const Comp = as as React.ElementType;
    return (
      <Comp
        ref={ref as React.Ref<HTMLElement>}
        data-slot="pill"
        className={cn(pillVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Pill.displayName = "Pill";

export { Pill, pillVariants };
export type { PillProps };
